<!-- Backdrop -->
<div *ngIf="isOpen"
  class="cart-backdrop fixed inset-0 bg-black/40 z-[49998]"
  aria-hidden="true"
  [class.cart-backdrop-closing]="isClosing"
  (click)="onClose(); $event.stopPropagation()">
</div>

<!-- Cart Sidebar Drawer -->
<div *ngIf="isOpen"
  #cartDrawer
  id="cart-drawer"
  role="dialog"
  aria-modal="true"
  aria-labelledby="cart-drawer-title"
  tabindex="-1"
  class="cart-sidebar fixed top-0 right-0 h-full w-full sm:w-[460px] bg-white z-[49999] flex flex-col shadow-2xl"
  [class.cart-sidebar-closing]="isClosing"
  (click)="$event.stopPropagation()">

  <ng-container *ngIf="cartView$ | async as cartView">
  <!-- Header -->
  <div class="cart-header flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
    <div class="min-w-0">
      <h2 id="cart-drawer-title" class="text-base font-bold text-gray-900 leading-tight">{{ 'Koszyk' | translate }}</h2>
      <p class="cart-header-meta text-xs text-gray-400 mt-0.5">
        {{ 'Łącznie:' | translate }} {{ cartView.quantity }} {{ 'szt., pozycji w koszyku:' | translate }} {{ cartView.cart.length }}
      </p>
    </div>
    <button #closeCartButton (click)="onClose()"
      class="ml-3 w-8 h-8 flex flex-shrink-0 items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
      [attr.aria-label]="'Zamknij koszyk' | translate">
      <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>

  <p *ngIf="cartNotice" class="mx-4 mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800"
    role="status" aria-live="polite">{{ cartNotice }}</p>

  <!-- Empty State -->
  <div *ngIf="!cartView.cart.length" class="flex flex-col items-center justify-center flex-1 py-12 px-6 text-center">
    <div class="w-20 h-20 bg-gray-50 rounded-2xl flex items-center justify-center mb-5">
      <svg class="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
          d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2 8m2-8v8a2 2 0 002 2h6a2 2 0 002-2v-8" />
      </svg>
    </div>
    <h3 class="text-base font-bold text-gray-900 mb-1">{{ 'Koszyk jest pusty' | translate }}</h3>
    <p class="text-sm text-gray-400 mb-6">{{ 'Przejrzyj ofertę i dodaj produkty do koszyka.' | translate }}</p>
    <button routerLink="/products" (click)="onClose()"
      class="px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer">
      {{ 'Przeglądaj produkty' | translate }}
    </button>
    <div *ngIf="removedItemName" class="mt-4 box-border flex w-full min-w-0 max-w-full items-center gap-3 overflow-hidden rounded-lg bg-gray-900 px-3 py-2 text-sm text-white"
      role="status" aria-live="polite">
      <span class="min-w-0 flex-1 truncate">{{ 'Usunięto z koszyka:' | translate }} {{ removedItemName }}</span>
      <button type="button" (click)="undoRemove()"
        class="flex-shrink-0 font-semibold text-orange-300 underline underline-offset-2 hover:text-orange-200">
        {{ 'Cofnij' | translate }}
      </button>
    </div>
  </div>

  <!-- Cart has items -->
  <div *ngIf="cartView.cart.length" class="flex flex-col flex-1 min-h-0">

    <!-- Scrollable Item List -->
    <div class="flex-1 overflow-y-auto px-4 py-3 space-y-2.5 min-h-0 cart-items">
      <p *ngIf="quantityUpdateError" class="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
        role="alert">{{ quantityUpdateError }}</p>
      <div *ngIf="removedItemName" class="box-border flex w-full min-w-0 max-w-full items-center gap-3 overflow-hidden rounded-lg bg-gray-900 px-3 py-2 text-sm text-white"
        role="status" aria-live="polite">
        <span class="min-w-0 flex-1 truncate">{{ 'Usunięto z koszyka:' | translate }} {{ removedItemName }}</span>
        <button type="button" (click)="undoRemove()"
          class="flex-shrink-0 font-semibold text-orange-300 underline underline-offset-2 hover:text-orange-200">
          {{ 'Cofnij' | translate }}
        </button>
      </div>
      <div *ngFor="let item of cartView.cart; let itemIndex = index"
        class="flex gap-4 p-4 rounded-xl border border-gray-100 hover:border-orange-100 hover:bg-orange-50/30 transition-all duration-150 cart-item">

        <!-- Thumbnail -->
        <div class="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-gray-100 bg-gray-50 p-2 flex items-center justify-center">
          <img [src]="getProductImageUrl(item)" [alt]="getProductName(item)" (error)="onProductImageError($event)"
            class="max-w-full max-h-full object-contain" />
        </div>

        <!-- Details -->
        <div class="flex-1 min-w-0">
          <h3 class="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-2">
            {{ item?.product?.label }}
          </h3>

          <!-- Variants -->
          <div *ngIf="item?.variants" class="flex flex-wrap gap-1.5 mb-2">
            <span *ngFor="let variant of item?.variants | keyvalue"
              class="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-md">
              {{ getVariantLabel(variant.key, item?.product) }}: {{ getVariantValue(variant.value, getVariantLabel(variant.key, item?.product)) }}
            </span>
          </div>

          <!-- Combination Values -->
          <div *ngIf="item?.combination?.values" class="flex flex-wrap gap-1.5 mb-2">
            <span *ngFor="let v of item?.combination?.values"
              class="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-md">
              {{ v }}
            </span>
          </div>

          <!-- Color -->
          <div class="flex items-center gap-1.5 mb-2" *ngIf="item?.selectedColor">
            <div class="w-3 h-3 rounded-full border border-gray-200 flex-shrink-0"
              [style.background-color]="getColorHex(item?.selectedColor?.colorName)"></div>
            <span class="text-xs text-gray-500">{{ item?.selectedColor?.colorName }}</span>
          </div>

          <!-- Downloads -->
          <div *ngIf="item?.selectedDownloads?.length > 0" class="mb-2 space-y-1">
            <div *ngFor="let dl of item?.selectedDownloads"
              class="flex items-start justify-between gap-2 text-xs text-gray-500">
              <div class="flex items-start gap-1.5 min-w-0">
                <svg class="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span class="line-clamp-2 leading-tight">{{ dl.name }}</span>
              </div>
              <span *ngIf="dl.price > 0" class="text-green-600 font-semibold flex-shrink-0">+{{ dl.price | appendCurrency }}</span>
              <span *ngIf="!dl.price || dl.price === 0" class="text-gray-400 flex-shrink-0">{{ 'BEZPŁATNIE' | translate }}</span>
            </div>
          </div>

          <!-- Qty + Price -->
          <div class="flex flex-wrap items-end justify-between gap-2 mt-3">
            <div>
              <span class="block text-[11px] text-gray-400">{{ 'Cena jednostkowa przed zaokrągleniem' | translate }}</span>
              <span class="text-xs font-medium text-gray-600">{{ getFormattedUnitPrice(item) }}</span>
            </div>
            <div class="flex items-center gap-2" [attr.aria-label]="'Ilość produktu: ' + getProductName(item)">
              <button type="button" (click)="changeQuantity(item, -1, itemIndex)" [disabled]="!canDecreaseQuantity(item)"
                [attr.aria-label]="'Zmniejsz ilość produktu: ' + getProductName(item)"
                class="h-11 w-11 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40">
                <span aria-hidden="true">−</span>
              </button>
              <div class="cart-quantity-value flex h-11 min-w-8 items-center justify-center text-center text-sm font-semibold text-gray-700">
                {{ item?.quantity }}
              </div>
              <button type="button" (click)="changeQuantity(item, 1, itemIndex)"
                [disabled]="!canIncreaseQuantity(item)"
                [attr.aria-label]="'Zwiększ ilość produktu: ' + getProductName(item)"
                class="h-11 w-11 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40">
                <span aria-hidden="true">+</span>
              </button>
            </div>
            <p *ngIf="!canIncreaseQuantity(item)" class="w-full text-right text-[11px] text-gray-500">
              {{ getQuantityLimitMessage(item) }}
            </p>
            <div class="w-full text-right">
              <span class="text-[11px] text-gray-400">{{ 'Wartość pozycji' | translate }}</span>
              <span class="ml-2 text-sm font-bold text-gray-800">{{ getTotalHT(item) | appendCurrency }}</span>
            </div>
          </div>

          <!-- Volume discount badge -->
          <div *ngIf="item?.appliedVolumeDiscount"
            class="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-semibold rounded-md">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
            </svg>
            {{ 'Rabat ilościowy:' | translate }} {{ item?.appliedVolumeDiscount?.discountPercent }}%
          </div>
        </div>

        <!-- Delete -->
        <button type="button" (click)="onRemoveFromCart(item)"
          class="flex-shrink-0 self-start w-11 h-11 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors group cursor-pointer remove-btn focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
          [attr.aria-label]="'Usuń z koszyka: ' + getProductName(item)">
          <svg class="w-4 h-4 text-gray-300 group-hover:text-red-500 transition-colors" fill="none"
            stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Price Summary -->
    <div class="cart-summary flex-shrink-0 overflow-y-auto border-t border-gray-100 px-4 py-2 sm:px-6 sm:py-4 bg-gray-50/80 shadow-[inner_0_2px_4px_rgba(0,0,0,0.02)]">
      <div class="space-y-1.5 text-sm sm:space-y-2">
        <div class="cart-summary-row text-gray-500">
          <span class="font-medium">{{ 'Wartość produktów netto' | translate }}</span>
          <span class="cart-summary-amount font-bold text-gray-900">{{ cartView.summary.productsSubtotal | appendCurrency }}</span>
        </div>
        <div class="cart-summary-row text-gray-500" *ngIf="cartView.summary.downloadsSubtotal > 0">
          <span class="font-medium">{{ 'Usługi i opcje' | translate }}</span>
          <span class="cart-summary-amount font-bold text-gray-900">{{ cartView.summary.downloadsSubtotal | appendCurrency }}</span>
        </div>
        <div class="cart-summary-row cart-summary-delivery text-gray-500">
          <span class="font-medium">{{ 'Dostawa' | translate }}</span>
          <span class="cart-summary-note font-medium text-gray-600">{{ 'Obliczany przy kasie' | translate }}</span>
        </div>
        <div class="cart-summary-row text-gray-500">
          <span class="font-medium">{{ 'VAT (produkty i usługi)' | translate }}</span>
          <span class="cart-summary-amount font-bold text-gray-900">{{ cartView.summary.vat | appendCurrency }}</span>
        </div>
      </div>

      <!-- Grand Total -->
      <div class="cart-grand-total mt-2 pt-2 border-t border-gray-200 sm:mt-3 sm:pt-3">
        <span class="text-base font-extrabold text-gray-900">{{ 'Szacunkowa suma brutto' | translate }}</span>
        <span class="cart-grand-total-amount text-xl sm:text-2xl font-black text-orange-500">{{ getFormattedGrandTotal() }}</span>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="cart-actions flex-shrink-0 px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-100 bg-white flex gap-3"
      style="padding-bottom: max(0.75rem, env(safe-area-inset-bottom))">
      <button type="button" (click)="onClose()"
        class="flex-1 py-3.5 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 cursor-pointer active:scale-95">
        {{ 'Kontynuuj zakupy' | translate }}
      </button>
      <button routerLink="/checkout" (click)="onClose()"
        class="flex-1 py-3.5 text-sm font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-xl transition-all duration-200 shadow-md shadow-orange-100 cursor-pointer active:scale-95">
        {{ 'Przejdź do składania zamówienia' | translate }}
      </button>
    </div>
  </div>
  </ng-container>

</div>